import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, Button, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material_modern'
import { Info, InfoOutlined } from '@mui/icons-material'

import DataGrid from 'components/generic/datagrid'
import { useGetBatchQuery } from 'services/batches'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import Loading from 'components/generic/Loading'
import { dateFormatter, dateTimeFormatter } from 'utils/data-manipulation/valueFormatter'
import { setDecimalPrecision } from 'utils/helpers'
import { CURRENCY_PREPENDS } from 'utils/constants'
import { getUrl, syncObjectTypes, syncStatus } from '../utils'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { ModalBladeProps } from 'components/layouts/ContentLayout'
import { selectSyncLogInfoModalProps } from 'state/ui/selectors'

const getColumns = () => [
  {
    field: 'id',
    headerName: '#',
    renderCell: params => {
      const { row } = params
      if (
        row.objectType === syncObjectTypes.PAYMENT.name ||
        row.objectType === syncObjectTypes.BILL.name ||
        row.objectType === syncObjectTypes.INVOICE.name
      ) {
        return (
          <Link to={`${row.redirectUrl}`} target="_blank">
            {row.externalId}
          </Link>
        )
      }
      return row.externalId
    },
  },
  {
    field: 'objectType',
    headerName: 'Object Type',
    flex: 1,
  },
  {
    field: 'syncStatus',
    headerName: 'Sync Status',
    flex: 1,
    renderCell: params => {
      return (
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center">
            {syncStatus[params.row.syncStatus]?.icon()}
            {syncStatus[params.row.syncStatus]?.text}
          </Box>
          {params.row.userFacingErrorMsg && (
            <Tooltip title={params.row.userFacingErrorMsg} slotProps={{ tooltip: { sx: { fontSize: '0.9em' } } }}>
              <IconButton size="small">
                <Info />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    },
  },
  {
    field: 'customer',
    headerName: 'Customer',
    flex: 1,
  },
  {
    field: 'dateCreated',
    headerName: 'Date Created',
    flex: 1,
  },
  {
    field: 'value',
    headerName: 'Value',
    headerAlign: 'right',
    flex: 1,
    align: 'right',
  },
]

const SyncFailureMessage = () => {
  return (
    <Box
      sx={{
        border: '1px solid red',
        borderRadius: '4px',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#fff5f5',
      }}
    >
      <InfoOutlined sx={{ color: 'red', marginRight: '8px' }} />
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
        <Box>
          <Typography variant="h6" sx={{ color: 'brown' }}>
            Some items failed to sync
          </Typography>
          <Typography variant="body2" sx={{ color: 'brown' }}>
            Hover over the info icon for details
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export interface SyncLogInfoBladeProps {
  batchId: string
}

const SyncLogInfo: React.FC<ModalBladeProps> = ({ onFormStateChange, onHideModal }) => {
  const { defaultCurrency } = useAppSelector(state => state.settings)

  const { batchId } = useAppSelector(selectSyncLogInfoModalProps)
  const [batchNumber, setBatchNumber] = useState('')
  const [lastSyncedDateMessage, setLastSyncedDateMessage] = useState('')
  const [batchHasErrorMessage, setBatchHasErrorMessage] = useState(false)
  const { data, isLoading } = useGetBatchQuery(
    { batchId },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  // set blade header info
  useEffect(() => {
    if (data) {
      const { perYardBatchNumber, dateTime = null } = data || ''
      setBatchNumber(perYardBatchNumber as any)
      setLastSyncedDateMessage(`Synced on ${dateTimeFormatter(dateTime, { dateFormatFull: true })}`)

      data.syncStatus?.forEach(item => {
        // if any item has an error message, set the flag to true, so we can show the tip in header
        if (item.userFacingErrorMsg) setBatchHasErrorMessage(true)
      })
    }
  }, [data])

  const transformedRowsItems = (data?.syncStatus || [])
    .slice()
    .sort((a, b) => new Date(a.createdDateTime).getTime() - new Date(b.createdDateTime).getTime())
    .map(item => {
      const pricePrepend =
        !item.currency || defaultCurrency === item.currency
          ? CURRENCY_PREPENDS[defaultCurrency]?.hc
          : CURRENCY_PREPENDS[item.currency]?.fc
      return {
        id: item.id,
        externalId: item.externalId,
        redirectUrl: getUrl(item.itemType.toUpperCase(), item),
        objectType: syncObjectTypes[item.itemType]?.name,
        syncStatus: item.status,
        userFacingErrorMsg: item.userFacingErrorMsg,
        customer: item.customerCommonIdentifierString,
        dateCreated: item.createdDateTime ? dateFormatter(item.createdDateTime) : undefined,
        value: item.value ? `${pricePrepend}${setDecimalPrecision(item.value, { decimalPrecisionOverride: 2 })}` : '-',
      }
    })

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  if (isLoading) return <Loading localized />

  return (
    <Stack height="100vh">
      <FormModalHeading title={`Batch #${batchNumber}`} backButtonPosition="right" />
      <Divider />
      <Box p={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body1">{lastSyncedDateMessage}</Typography>
          {/*show outlined warning when batch has failures*/}
          {batchHasErrorMessage && (
            <Box maxWidth="50%">
              <SyncFailureMessage />
            </Box>
          )}
        </Stack>
      </Box>
      <Divider />
      <Box px={4} flexGrow={1}>
        <DataGrid
          rows={transformedRowsItems}
          columns={getColumns()}
          flexGrow={1}
          hideFooter
          disableSelectionOnclick
          onCellClick={(_, event) => event.stopPropagation()}
          styleProps={{
            '.MuiDataGrid-root': {
              paddingTop: 3,
              borderLeft: 0,
              borderRight: 0,
              borderTop: 0,
              height: 'inherit !important',
            },
            '.MuiDataGrid-columnHeader': {
              outlineOffset: 0,
              '&:focus': {
                outlineOffset: '0 !important',
              },
            },
          }}
        />
      </Box>
      <Divider />
      <Stack direction="row" p={2} spacing={2}>
        <Button variant="outlined" onClick={() => onHideModal()}>
          Close
        </Button>
      </Stack>
    </Stack>
  )
}

export default SyncLogInfo
